import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  LayoutDashboard,
  MessageSquare,
  Building2,
  FileCode,
  LogOut,
  Menu,
  X,
  ChevronRight
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_40c7321e-fdf9-4402-813d-639ea853bfad/artifacts/ve1f0xa3_logo%20SMH.png";

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/messages', icon: MessageSquare, label: 'Mensajes' },
  { path: '/clients', icon: Building2, label: 'Clientes' },
  { path: '/api-docs', icon: FileCode, label: 'API Docs' },
];

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavItem = ({ item }) => (
    <NavLink
      to={item.path}
      onClick={onClose}
      className={({ isActive }) =>
        `flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 group ${
          isActive
            ? 'bg-[#0066CC] text-white shadow-md'
            : 'text-[#6B7B8C] hover:bg-[#F0F2F5] hover:text-[#2C3E50]'
        }`
      }
      data-testid={`nav-${item.label.toLowerCase()}`}
    >
      <item.icon className="h-5 w-5 mr-3" strokeWidth={1.5} />
      <span className="flex-1">{item.label}</span>
      <ChevronRight className={`h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity`} />
    </NavLink>
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-[#E9EDEF] transform transition-transform duration-200 lg:transform-none ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-[#E9EDEF]">
            <img 
              src={LOGO_URL} 
              alt="SmartBoat MessageHub" 
              className="h-10 object-contain"
              data-testid="sidebar-logo"
            />
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-1">
              {navItems.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </nav>
          </ScrollArea>

          {/* User & Logout */}
          <div className="p-4 border-t border-[#E9EDEF]">
            <div className="flex items-center mb-3">
              <div className="h-10 w-10 rounded-full bg-[#0066CC] flex items-center justify-center text-white font-semibold">
                {user?.name?.charAt(0).toUpperCase() || 'A'}
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-[#2C3E50] truncate">{user?.name}</p>
                <p className="text-xs text-[#6B7B8C] truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start text-[#6B7B8C] hover:text-red-600 hover:border-red-200 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  return (
    <div className="flex h-screen bg-[#F0F2F5]">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="h-16 flex items-center px-4 bg-white border-b border-[#E9EDEF] lg:hidden">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <img 
            src={LOGO_URL} 
            alt="SMH" 
            className="h-8 ml-3"
          />
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
